import { useState, useEffect, useRef } from "react";
import { Globe } from "lucide-react";
import { apiGet } from "@/lib/api";

const FALLBACK_NEWS = [
  { formatted: "EU tightens sustainability reporting requirements", category: "regulation" },
  { formatted: "Climate VCs increase investment activity 40%", category: "funding" },
  { formatted: "CSRD scope expanding to supply chains", category: "regulation" },
  { formatted: "B2B buyers require verified ESG data", category: "market" },
  { formatted: "Green bond issuance surges across Europe", category: "funding" },
  { formatted: "Carbon border tax reshapes import costs", category: "regulation" },
  { formatted: "Leading firms report Scope 3 emissions", category: "competition" },
  { formatted: "New sustainability standard published", category: "regulation" },
  { formatted: "Supply chain due diligence directive enforced", category: "regulation" },
  { formatted: "ESG-linked lending grows 60% year-over-year", category: "funding" },
  { formatted: "Green Claims Directive bans unverified labels", category: "regulation" },
  { formatted: "Climate disclosure becomes procurement baseline", category: "market" },
];

const CATEGORY_CONFIG = {
  regulation: { label: "Regulation", color: "rgba(96, 165, 250, 0.9)", bg: "rgba(96, 165, 250, 0.12)" },
  market: { label: "Market", color: "rgba(251, 191, 36, 0.9)", bg: "rgba(251, 191, 36, 0.12)" },
  funding: { label: "Funding", color: "rgba(74, 222, 128, 0.9)", bg: "rgba(74, 222, 128, 0.12)" },
  competition: { label: "Competition", color: "rgba(239, 68, 68, 0.8)", bg: "rgba(239, 68, 68, 0.12)" },
  opportunity: { label: "Opportunity", color: "rgba(168, 85, 247, 0.9)", bg: "rgba(168, 85, 247, 0.12)" },
  // Legacy
  eu_policy: { label: "Policy", color: "rgba(96, 165, 250, 0.9)", bg: "rgba(96, 165, 250, 0.12)" },
  country: { label: "Policy", color: "rgba(45, 212, 191, 0.9)", bg: "rgba(45, 212, 191, 0.12)" },
  region: { label: "Market", color: "rgba(251, 191, 36, 0.9)", bg: "rgba(251, 191, 36, 0.12)" },
  global: { label: "Global", color: "rgba(156, 163, 175, 0.8)", bg: "rgba(156, 163, 175, 0.12)" },
};

export function CommunityTicker({ industry = "general", country = "", city = "", size = "", companyId = "" }) {
  const [news, setNews] = useState(FALLBACK_NEWS);
  const [loaded, setLoaded] = useState(false);
  const tickerRef = useRef(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        const params = new URLSearchParams({
          industry: industry || "general",
          country: country || "",
          city: city || "",
          size: size || "",
          company_id: companyId || ""
        });
        const data = await apiGet(`/api/community/news?${params.toString()}`);
        if (data.news && data.news.length >= 5) {
          setNews(data.news);
        }
      } catch (err) {
        // Fallback ticker used
      } finally {
        setLoaded(true);
      }
    };
    fetchNews();
  }, [industry, country, city, size, companyId]);

  // Triple the items for seamless infinite scroll
  const items = [...news, ...news, ...news];

  return (
    <div className="community-ticker-wrapper" data-testid="community-ticker">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-primary/70" />
        <span className="text-xs font-semibold uppercase tracking-wider text-white/50">
          Community Updates
        </span>
      </div>
      
      <div className="community-ticker-container">
        <div 
          ref={tickerRef}
          className={`community-ticker ${loaded ? 'animate' : ''}`}
        >
          {items.map((item, idx) => {
            const cat = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.market;
            
            return (
              <span key={`tk-${item.formatted?.slice(0,15)}-${idx}`} className="ticker-item">
                <span 
                  className="category-pill"
                  style={{ 
                    color: cat.color, 
                    backgroundColor: cat.bg,
                    borderColor: cat.color.replace('0.8', '0.25').replace('0.9', '0.25')
                  }}
                >
                  {cat.label}
                </span>
                <span className="ticker-text">{item.formatted}</span>
                <span className="ticker-separator">
                  <span className="separator-dot"></span>
                </span>
              </span>
            );
          })}
        </div>
      </div>

      <style>{`
        .community-ticker-wrapper {
          padding: 20px 0;
          margin-bottom: 8px;
        }
        
        .community-ticker-container {
          overflow: hidden;
          position: relative;
          padding: 8px 0;
        }
        
        .community-ticker-container::before,
        .community-ticker-container::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          width: 120px;
          z-index: 2;
          pointer-events: none;
        }
        
        .community-ticker-container::before {
          left: 0;
          background: linear-gradient(to right, 
            rgba(2, 4, 2, 1) 0%, 
            rgba(2, 4, 2, 0.8) 40%,
            rgba(2, 4, 2, 0) 100%);
        }
        
        .community-ticker-container::after {
          right: 0;
          background: linear-gradient(to left, 
            rgba(2, 4, 2, 1) 0%, 
            rgba(2, 4, 2, 0.8) 40%,
            rgba(2, 4, 2, 0) 100%);
        }
        
        .community-ticker {
          display: flex;
          white-space: nowrap;
          will-change: transform;
        }
        
        .community-ticker.animate {
          animation: ticker-scroll 11s linear infinite;
        }
        
        .community-ticker:hover {
          animation-play-state: paused;
        }
        
        .ticker-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 0 6px;
          flex-shrink: 0;
        }
        
        .category-pill {
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 3px 8px;
          border-radius: 4px;
          border: 1px solid;
          white-space: nowrap;
        }
        
        .ticker-text {
          font-size: 13px;
          font-weight: 500;
          color: rgba(255, 255, 255, 0.7);
          margin-left: 4px;
        }
        
        .ticker-separator {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin: 0 16px;
        }
        
        .separator-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(74, 222, 128, 0.4);
          box-shadow: 0 0 8px rgba(74, 222, 128, 0.4), 
                      0 0 16px rgba(74, 222, 128, 0.2);
        }
        
        @keyframes ticker-scroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
      `}</style>
    </div>
  );
}
